import { calculateGst, toDecimal } from '@mm/shared';
import clsx from 'clsx';
import { Plus, Trash2, Undo2 } from 'lucide-react';
import { Badge, Card, EmptyState } from '../../components/ui/display';
import { Button } from '../../components/ui/Button';
import { SearchableSelect } from '../../components/ui/SearchableSelect';
import type { Lookups } from '../../types/models';
import { formatMoney } from '../../utils/format';
import { trimRate } from '../masters/configs';

// ── API shapes ───────────────────────────────────────────────────────

export interface OtherServerRow {
  id: string;
  categoryId: string;
  appId: string | null;
  amountBeforeGst: string;
  gstRate: string;
  gstAmount: string;
  totalAmount: string;
  currencyCode: string;
  amountBeforeGstInr: string;
  gstAmountInr: string;
  totalAmountInr: string;
  notes: string | null;
  updatedAt: string;
  category: { id: string; name: string; code: string };
  app: { id: string; name: string; iconUrl: string | null } | null;
}

// ── Row state ────────────────────────────────────────────────────────

export interface OtherRowState {
  key: string;
  /** The saved entry this row was loaded from (null = new row). */
  server: OtherServerRow | null;
  categoryId: string;
  /** '' = company-level. */
  appId: string;
  /** '' means "not entered" — the row is not saved. */
  amount: string;
  gstRate: string;
  remove: boolean;
}

const AMOUNT_RE = /^\d{1,14}(\.\d{1,4})?$/;
let seq = 0;
const newKey = () => `new-${++seq}`;

export function initialOtherRows(rows: OtherServerRow[]): OtherRowState[] {
  return rows.map((r) => ({
    key: r.id,
    server: r,
    categoryId: r.categoryId,
    appId: r.appId ?? '',
    amount: String(Number(r.amountBeforeGst)),
    gstRate: trimRate(r.gstRate),
    remove: false,
  }));
}

export const blankOtherRow = (gstRate: string, from?: Partial<OtherRowState>): OtherRowState => ({
  key: newKey(),
  server: null,
  categoryId: from?.categoryId ?? '',
  appId: from?.appId ?? '',
  amount: '',
  gstRate: from?.gstRate ?? gstRate,
  remove: false,
});

export type OtherRowStatus = 'saved' | 'changed' | 'new' | 'remove' | 'invalid' | 'draft';

export function otherRowStatus(r: OtherRowState): OtherRowStatus {
  if (r.remove) return 'remove';
  const e = r.server;
  if (e && e.currencyCode !== 'INR') return 'saved'; // edited on the Expenses page only
  if (r.amount !== '' && (!AMOUNT_RE.test(r.amount) || !r.categoryId)) return 'invalid';
  if (!e) return r.amount === '' ? 'draft' : 'new';
  if (r.amount === '') return 'invalid';
  const same =
    toDecimal(r.amount).equals(e.amountBeforeGst) &&
    toDecimal(r.gstRate || '0').equals(e.gstRate) &&
    r.categoryId === e.categoryId &&
    (r.appId || null) === e.appId;
  return same ? 'saved' : 'changed';
}

/** INR preview with the same Decimal rules as the server. */
export function otherPreview(r: OtherRowState) {
  if (r.remove) return null;
  if (r.server?.currencyCode && r.server.currencyCode !== 'INR') {
    return {
      before: toDecimal(r.server.amountBeforeGstInr),
      gst: toDecimal(r.server.gstAmountInr),
      total: toDecimal(r.server.totalAmountInr),
    };
  }
  if (r.amount === '' || !AMOUNT_RE.test(r.amount)) return null;
  const g = calculateGst(r.amount, r.gstRate || '0');
  return { before: g.beforeGst, gst: g.gstAmount, total: g.totalAmount };
}

const STATUS_UI: Record<
  OtherRowStatus,
  { label: string; tone: 'green' | 'amber' | 'blue' | 'slate' | 'rose' }
> = {
  saved: { label: 'Saved', tone: 'green' },
  changed: { label: 'Changed', tone: 'amber' },
  new: { label: 'New', tone: 'blue' },
  draft: { label: 'Not entered', tone: 'slate' },
  remove: { label: 'Will be deleted', tone: 'rose' },
  invalid: { label: 'Check row', tone: 'rose' },
};

/** Section 3 of morning entry: other (non-marketing) expenses for the day, INR. */
export function OtherSection({
  rows,
  statuses,
  lookups,
  defaultGstRate,
  onChange,
  onAdd,
  onDiscard,
}: {
  rows: OtherRowState[];
  statuses: Map<string, OtherRowStatus>;
  lookups: Lookups | undefined;
  defaultGstRate: string;
  onChange: (key: string, patch: Partial<OtherRowState>) => void;
  onAdd: () => void;
  onDiscard: (key: string) => void;
}) {
  const categories = (lookups?.expenseCategories ?? []).filter((c) => c.status === 'ACTIVE');
  const categoryOptions = categories.map((c) => ({ value: c.id, label: c.name }));
  const appOptions = [
    { value: '', label: 'Company-level' },
    ...(lookups?.apps ?? [])
      .filter((a) => a.status === 'ACTIVE')
      .map((a) => ({ value: a.id, label: a.name })),
  ];
  const rateOptions = (lookups?.gstRates ?? [])
    .filter((g) => g.status === 'ACTIVE')
    .map((g) => ({ value: trimRate(g.rate), label: g.label }));

  return (
    <Card
      title="3. Other expenses"
      description="Salary, servers, software and other costs paid today (INR). Leave the amount empty to skip a row; foreign-currency bills go on the Expenses page."
      bodyClassName="p-0"
      actions={
        <Button variant="secondary" size="sm" icon={<Plus className="size-4" />} onClick={onAdd}>
          Add expense
        </Button>
      }
    >
      {rows.length === 0 ? (
        <EmptyState
          title="No other expenses for this day"
          description="Use “Add expense” when there is one."
        />
      ) : (
        <div className="relative overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-xs font-semibold tracking-wide text-slate-500 uppercase">
              <tr>
                <th className="px-4 py-3 text-left">Category</th>
                <th className="px-4 py-3 text-left">App</th>
                <th className="px-4 py-3 text-right">Amount (₹, before GST)</th>
                <th className="px-4 py-3 text-left">GST rate</th>
                <th className="px-4 py-3 text-right text-violet-700">GST</th>
                <th className="px-4 py-3 text-right">Total</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-2 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r) => {
                const st = statuses.get(r.key)!;
                const p = otherPreview(r);
                const foreign = !!r.server && r.server.currencyCode !== 'INR';
                const locked = r.remove || foreign;
                return (
                  <tr
                    key={r.key}
                    className={clsx(r.remove && 'bg-rose-50/60 text-slate-400 line-through')}
                  >
                    <td className="px-4 py-2">
                      <SearchableSelect
                        ariaLabel="Expense category"
                        className="w-44"
                        placeholder="Category…"
                        disabled={locked}
                        value={r.categoryId}
                        options={categoryOptions}
                        onChange={(v) => onChange(r.key, { categoryId: v })}
                      />
                    </td>
                    <td className="px-4 py-2">
                      <SearchableSelect
                        ariaLabel="App"
                        className="w-48"
                        disabled={locked}
                        value={r.appId}
                        options={appOptions}
                        onChange={(v) => onChange(r.key, { appId: v })}
                      />
                    </td>
                    <td className="px-4 py-2 text-right">
                      {foreign ? (
                        <span className="num text-xs">
                          {formatMoney(r.server!.amountBeforeGst, r.server!.currencyCode)}
                        </span>
                      ) : (
                        <input
                          aria-label="Expense amount"
                          inputMode="decimal"
                          autoComplete="off"
                          disabled={locked}
                          value={r.amount}
                          placeholder="—"
                          onChange={(e) =>
                            onChange(r.key, { amount: e.target.value.replace(/,/g, '').trim() })
                          }
                          onFocus={(e) => e.target.select()}
                          className={clsx(
                            'num h-9 w-32 rounded-md border px-2 text-right focus:ring-2 focus:outline-none',
                            st === 'invalid'
                              ? 'border-rose-400 focus:ring-rose-200'
                              : 'border-slate-300 focus:border-brand-500 focus:ring-brand-500/20',
                          )}
                        />
                      )}
                    </td>
                    <td className="px-4 py-2">
                      <SearchableSelect
                        ariaLabel="GST rate"
                        className="w-28"
                        disabled={locked}
                        value={r.gstRate || defaultGstRate}
                        options={rateOptions}
                        onChange={(v) => onChange(r.key, { gstRate: v })}
                      />
                    </td>
                    <td className="num px-4 py-2 text-right text-violet-700">
                      {p ? formatMoney(p.gst.toString()) : '—'}
                    </td>
                    <td className="num px-4 py-2 text-right font-medium text-slate-900">
                      {p ? formatMoney(p.total.toString()) : '—'}
                    </td>
                    <td className="px-4 py-2">
                      <Badge tone={STATUS_UI[st].tone}>{STATUS_UI[st].label}</Badge>
                    </td>
                    <td className="px-2 py-2 text-right">
                      {r.server ? (
                        <button
                          type="button"
                          onClick={() => onChange(r.key, { remove: !r.remove })}
                          className="rounded-md p-1.5 text-slate-400 hover:bg-rose-50 hover:text-rose-600"
                          aria-label={r.remove ? 'Keep saved expense' : 'Delete saved expense'}
                          title={r.remove ? 'Keep saved expense' : 'Delete saved expense'}
                        >
                          {r.remove ? <Undo2 className="size-4" /> : <Trash2 className="size-4" />}
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => onDiscard(r.key)}
                          className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                          aria-label="Remove row"
                          title="Remove row (nothing is saved)"
                        >
                          <Trash2 className="size-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
